import { useEffect } from "react";

import { useEmbedMode } from "./useEmbedMode";
import {
  STUDY_EMBED_SOURCE,
  clearEmbedUnsavedWork,
  embedCloseAnswer,
  postStudyEmbedDraft,
  postStudyEmbedMessage,
  publishEmbedUnsavedWork,
} from "./studyEmbedBridge";

/**
 * Mounted by the creation flow and the results page. The parent asks
 * `{type:'close-request'}`; the answer is close-blocked only while the
 * builder has published unsaved work. Review and results leave that unset.
 */
export function useStudyEmbedBridge(): void {
  const isEmbed = useEmbedMode();

  useEffect(() => {
    if (!isEmbed) {
      return;
    }
    function onMessage(event: MessageEvent) {
      if (event.origin !== window.location.origin) {
        return;
      }
      if (event.source !== window.parent) {
        return;
      }
      if (!isCloseRequest(event.data)) {
        return;
      }
      const type = embedCloseAnswer();
      postStudyEmbedMessage({ source: STUDY_EMBED_SOURCE, type });
    }
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
    };
  }, [isEmbed]);
}

/** The builder reports unsaved edits. Unmount clears them, so review answers close-ok. */
export function usePublishEmbedUnsavedWork(hasUnsavedWork: boolean): void {
  const isEmbed = useEmbedMode();

  useEffect(() => {
    if (!isEmbed) {
      clearEmbedUnsavedWork();
      return;
    }
    publishEmbedUnsavedWork(hasUnsavedWork);
    return () => {
      clearEmbedUnsavedWork();
    };
  }, [hasUnsavedWork, isEmbed]);
}

/** Posts `{type:'draft'}` the first time this document sees a draft id. */
export function usePublishStudyEmbedDraft(draftToken: string | null): void {
  const isEmbed = useEmbedMode();

  useEffect(() => {
    if (!isEmbed || !draftToken) {
      return;
    }
    postStudyEmbedDraft(draftToken);
  }, [draftToken, isEmbed]);
}

function isCloseRequest(data: unknown): boolean {
  if (!data || typeof data !== "object") {
    return false;
  }
  return (data as { type?: unknown }).type === "close-request";
}
